import { codeExample, defineDemoElement, demoLayoutStyles, stripIndent } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import type { DemoSection } from './demo-shared.js';
import type WebmoduleAlert from '../../components/alert/alert.js';
import type WebmoduleButton from '../../components/button/button.js';

/** Fires toast notifications from pre-rendered (closed) alerts. */
class DemoAlertToasts extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }
    `
  ];

  render() {
    return html`
      <div class="demo-row" @click=${(e: PointerEvent) => this.showToast(e)}>
        <webmodule-button variant="primary">Primary toast</webmodule-button>
        <webmodule-button variant="success">Success toast</webmodule-button>
        <webmodule-button variant="warning">Warning toast</webmodule-button>
        <webmodule-button variant="danger">Danger toast</webmodule-button>
      </div>

      <webmodule-alert variant="primary" duration="3000"><strong>Primary toast</strong></webmodule-alert>
      <webmodule-alert variant="success" duration="3000" closable>
        <strong>Success toast that is closable</strong>
      </webmodule-alert>
      <webmodule-alert variant="warning" duration="3000" closable>
        <strong>Warning toast that is closable</strong>
      </webmodule-alert>
      <webmodule-alert variant="danger" duration="3000"><strong>Danger toast</strong></webmodule-alert>
    `;
  }

  private showToast(e: PointerEvent) {
    const button = e.target as WebmoduleButton;

    if (button.tagName?.toLowerCase() !== 'webmodule-button') {
      return;
    }

    const alert = this.shadowRoot!.querySelector<WebmoduleAlert>(
      `webmodule-alert[variant='${button.variant}']:not([open])`
    );
    alert?.toast();
  }
}

export function registerFeedbackDemos() {
  defineDemoElement('demo-alert-toasts', DemoAlertToasts);
}

export const alertSection: DemoSection = {
  id: 'alert',
  title: 'Alert',
  tags: ['webmodule-alert'],
  summary:
    'Inline messages for statuses and outcomes. The same element doubles as a toast: call toast() on a closed alert ' +
    'and it stacks itself in the notification region until its duration elapses.',
  render: () => html`
    ${codeExample({
      label: 'Variants',
      code: `
        <div class="demo-stack">
          <webmodule-alert variant="primary" open>variant="primary"</webmodule-alert>
          <webmodule-alert variant="success" open>variant="success"</webmodule-alert>
          <webmodule-alert variant="neutral" open>variant="neutral"</webmodule-alert>
          <webmodule-alert variant="warning" open>variant="warning"</webmodule-alert>
          <webmodule-alert variant="danger" open>variant="danger"</webmodule-alert>
        </div>`
    })}
    ${codeExample({
      label: 'Closable and custom icon',
      code: `
        <div class="demo-stack">
          <webmodule-alert variant="primary" open closable>The closable attribute adds a dismiss button.</webmodule-alert>
          <webmodule-alert variant="danger" open>
            <webmodule-icon slot="icon" name="x-circle-fill"></webmodule-icon>
            An element in the icon slot replaces the default icon.
          </webmodule-alert>
        </div>`
    })}
    ${codeExample({
      label: 'Snack style',
      description: 'The snack attribute renders a compact, border-free variant for dense surfaces.',
      code: `
        <div class="demo-stack">
          <webmodule-alert variant="success" open snack>variant="success" snack</webmodule-alert>
          <webmodule-alert variant="warning" open snack>variant="warning" snack</webmodule-alert>
        </div>`
    })}

    <demo-example
      label="Toast notifications"
      .code=${stripIndent(`
        <webmodule-alert variant="success" duration="3000" closable>
          <strong>Saved!</strong>
        </webmodule-alert>

        // Render the alert closed, then:
        alert.toast();`)}
    >
      <demo-alert-toasts></demo-alert-toasts>
    </demo-example>
  `
};

export const spinnerSection: DemoSection = {
  id: 'spinner',
  title: 'Spinner',
  tags: ['webmodule-spinner'],
  summary: 'An indeterminate progress indicator that scales with font-size.',
  render: () => html`
    ${codeExample({
      label: 'Sizing and theming',
      code: `
        <div class="demo-row" style="gap: var(--webmodule-spacing-x-large);">
          <webmodule-spinner></webmodule-spinner>
          <webmodule-spinner style="font-size: 2rem;"></webmodule-spinner>
          <webmodule-spinner style="font-size: 3rem; --track-width: 6px;"></webmodule-spinner>
          <webmodule-spinner
            style="font-size: 3rem; --indicator-color: var(--webmodule-color-success-600); --speed: 4s;"
          ></webmodule-spinner>
        </div>`
    })}
  `
};

export const placeholderSection: DemoSection = {
  id: 'placeholder',
  title: 'Placeholder',
  tags: ['webmodule-placeholder'],
  summary: 'Skeleton blocks shown while real content loads. Add shimmer for an animated sheen.',
  render: () => html`
    ${codeExample({
      label: 'Skeleton composition',
      description: 'Compose placeholders to sketch the loading layout; shape them with the display part.',
      code: `
        <div style="max-width: 420px;">
          <div class="demo-row" style="flex-wrap: nowrap; align-items: flex-start;">
            <webmodule-placeholder shimmer style="width: 3rem; height: 3rem; flex: none;"></webmodule-placeholder>
            <div style="flex: 1 1 auto; display: flex; flex-direction: column; gap: 0.5rem;">
              <webmodule-placeholder shimmer></webmodule-placeholder>
              <webmodule-placeholder shimmer></webmodule-placeholder>
              <webmodule-placeholder shimmer style="width: 60%;"></webmodule-placeholder>
            </div>
          </div>
        </div>`
    })}
    ${codeExample({
      label: 'Static (no shimmer)',
      code: `
        <div style="max-width: 420px; display: flex; flex-direction: column; gap: 0.5rem;">
          <webmodule-placeholder></webmodule-placeholder>
          <webmodule-placeholder style="width: 70%;"></webmodule-placeholder>
        </div>`
    })}
  `
};

export const tooltipSection: DemoSection = {
  id: 'tooltip',
  title: 'Tooltip',
  tags: ['webmodule-tooltip'],
  summary: 'Contextual hints anchored to their target element, with twelve placement options.',
  render: () => html`
    ${codeExample({
      label: 'Placement',
      code: `
        <div class="demo-row">
          <webmodule-tooltip content="top" placement="top">
            <webmodule-button size="small">top</webmodule-button>
          </webmodule-tooltip>
          <webmodule-tooltip content="bottom" placement="bottom">
            <webmodule-button size="small">bottom</webmodule-button>
          </webmodule-tooltip>
          <webmodule-tooltip content="left" placement="left">
            <webmodule-button size="small">left</webmodule-button>
          </webmodule-tooltip>
          <webmodule-tooltip content="right" placement="right">
            <webmodule-button size="small">right</webmodule-button>
          </webmodule-tooltip>
          <webmodule-tooltip content="top-start" placement="top-start">
            <webmodule-button size="small">top-start</webmodule-button>
          </webmodule-tooltip>
          <webmodule-tooltip content="top-end" placement="top-end">
            <webmodule-button size="small">top-end</webmodule-button>
          </webmodule-tooltip>
          <webmodule-tooltip content="bottom-start" placement="bottom-start">
            <webmodule-button size="small">bottom-start</webmodule-button>
          </webmodule-tooltip>
          <webmodule-tooltip content="bottom-end" placement="bottom-end">
            <webmodule-button size="small">bottom-end</webmodule-button>
          </webmodule-tooltip>
        </div>`
    })}
    ${codeExample({
      label: 'Rich content',
      description: 'Use the content slot for markup; --max-width controls wrapping.',
      code: `
        <div class="demo-row">
          <webmodule-tooltip style="--max-width: 220px;">
            <div slot="content">
              <strong>Formatted tooltip</strong><br />
              Slotted content supports any markup, wrapping at --max-width.
            </div>
            <webmodule-button size="small">Hover me</webmodule-button>
          </webmodule-tooltip>
        </div>`
    })}
  `
};
